import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  TablePagination,
} from "@mui/material";
import { MdMoreVert } from "react-icons/md";

const getStatusColor = (status) => {
  const normalized = `${status || ""}`.toLowerCase();
  if (["paid", "success", "completed", "approved"].includes(normalized)) {
    return { bg: "#DCFCE7", color: "#16A34A" };
  }
  if (["pending", "processing"].includes(normalized)) {
    return { bg: "#FEF3C7", color: "#F59E0B" };
  }
  if (
    ["failed", "declined", "reversed", "cancelled", "canceled"].includes(
      normalized,
    )
  ) {
    return { bg: "#FEE2E2", color: "#DC2626" };
  }
  return { bg: "#F1F5F9", color: "#64748B" };
};

const getTypeBadgeColor = (type) => {
  switch (type) {
    case "Subscription":
      return { bg: "#DBEAFE", color: "#1F4FFF" };
    case "Team Seats":
      return { bg: "#F3E8FF", color: "#9333EA" };
    case "Service Fee":
      return { bg: "#D1FAE5", color: "#059669" };
    case "Add-on":
      return { bg: "#FEE2E2", color: "#DC2626" };
    default:
      return { bg: "#F1F5F9", color: "#64748B" };
  }
};

const getDisplayName = (transaction) => {
  const account = transaction?.account || {};
  const firstName = account.firstName || account.first_name;
  const lastName = account.lastName || account.last_name;
  return [firstName, lastName].filter(Boolean).join(" ") || "Unknown";
};

const getDisplayEmail = (transaction) => {
  const account = transaction?.account || {};
  return account.email || "-";
};

const formatAmount = (amount, currency) => {
  const numeric = Number(amount);
  const isNumber = Number.isFinite(numeric);
  const symbol = currency === "NGN" ? "₦" : currency || "";
  const value = isNumber ? numeric.toLocaleString() : amount ?? "-";
  return `${symbol}${value}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-CA");
};

export default function BillingTransactionsTable({
  transactions,
  loading,
  page,
  limit,
  totalCount,
  onPageChange,
  onLimitChange,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                User
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Type
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Amount
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Plan
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Method
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography className="text-slate-600 py-6 text-center">
                    Loading transactions...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography className="text-slate-600 py-6 text-center">
                    No transactions found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => {
                const statusColor = getStatusColor(transaction.status);
                const typeColor = getTypeBadgeColor(transaction.reason);
                const displayName = getDisplayName(transaction);
                const displayEmail = getDisplayEmail(transaction);
                const initials = displayName?.charAt(0) || "?";
                return (
                  <TableRow
                    key={transaction._id || transaction.id}
                    sx={{
                      "&:hover": {
                        bgcolor: "#F8FAFC",
                      },
                    }}
                  >
                    <TableCell>
                      <Box className="flex items-center gap-2">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "#1F4FFF",
                            fontSize: "0.875rem",
                          }}
                        >
                          {initials}
                        </Avatar>
                        <Box>
                          <Typography className="font-semibold text-slate-900 text-sm">
                            {displayName}
                          </Typography>
                          <Typography className="text-xs text-slate-500">
                            {displayEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.reason || "Subscription"}
                        size="small"
                        sx={{
                          bgcolor: typeColor.bg,
                          color: typeColor.color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography className="font-bold text-slate-900">
                        {formatAmount(
                          transaction.amount,
                          transaction.currency,
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-700">
                        {transaction.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-700">
                        {transaction.paymentMode || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status || "-"}
                        size="small"
                        sx={{
                          bgcolor: statusColor.bg,
                          color: statusColor.color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography className="text-slate-700">
                        {formatDate(transaction.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <MdMoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        rowsPerPage={limit}
        onRowsPerPageChange={(event) => {
          const newLimit = parseInt(event.target.value, 10);
          onLimitChange(newLimit);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Paper>
  );
}
